import { useEffect, useRef, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { apiRequest } from "../../services/api";
import { hasPermission } from "../../utils/permissions";
const control =
  "mt-2 h-12 w-full rounded-xl border border-gray-200 bg-white px-4";
export default function ReturnCredit({
  company,
  returnId,
  postingDate,
  totals,
  acceptedCredit,
  creditHistory = [],
  remainingCredit,
  creditHold,
  onAccepted,
}) {
  const { accessToken, user } = useAuth();
  const canPost = hasPermission(user, "accounting.vouchers_post");
  const [form, setForm] = useState({
      noteNumber: "",
      noteDate: "",
      goods: "",
      cgst: "",
      sgst: "",
      igst: "",
      reviewNote: "",
    }),
    [preview, setPreview] = useState(null),
    [creditVerified, setCreditVerified] = useState(false),
    [taxVerified, setTaxVerified] = useState(false),
    [busy, setBusy] = useState(false),
    [error, setError] = useState(""),
    [notice, setNotice] = useState("");
  const generation = useRef(0);
  const remaining = remainingCredit || totals;
  useEffect(() => {
    const requests = generation; // Stable request counter, not a DOM ref.
    requests.current++;
    setPreview(null);
    setCreditVerified(false);
    setTaxVerified(false);
    setBusy(false);
    setError("");
    setNotice("");
    setForm({
      noteNumber: "",
      noteDate: "",
      goods: "",
      cgst: "",
      sgst: "",
      igst: "",
      reviewNote: "",
    });
    return () => {
      requests.current++;
    };
  }, [company, returnId, postingDate, accessToken, remaining.goods, remaining.cgst, remaining.sgst, remaining.igst, creditHold?.eventId]);
  function change(key, value) {
    setForm((f) => ({ ...f, [key]: value }));
    setPreview(null);
    setCreditVerified(false);
    setTaxVerified(false);
    setError("");
  }
  async function load(e) {
    e.preventDefault();
    if (busy) return;
    const seq = ++generation.current;
    setBusy(true);
    setError("");
    setPreview(null);
    setCreditVerified(false);
    setTaxVerified(false);
    try {
      const result = await apiRequest(
        `/accounting/${company}/supplier-returns/${returnId}/credit-preview`,
        { method: "POST", accessToken, body: { ...form, date: postingDate } },
      );
      if (seq === generation.current) setPreview(result);
    } catch (e) {
      if (seq === generation.current) setError(e.message);
    } finally {
      if (seq === generation.current) setBusy(false);
    }
  }
  async function accept() {
    if (busy || !preview || !canPost || !creditVerified || !taxVerified) return;
    const seq = ++generation.current;
    setBusy(true);
    setError("");
    try {
      const result = await apiRequest(
        `/accounting/${company}/supplier-returns/${returnId}/accept-credit`,
        {
          method: "POST",
          accessToken,
          body: {
            ...form,
            date: postingDate,
            previewHash: preview.previewHash,
            creditVerified,
            taxTreatmentVerified: taxVerified,
          },
        },
      );
      if (seq !== generation.current) return;
      setNotice(
        `Accepted ${form.noteNumber} with ${result.voucher.number}. Credit remains unapplied; allocate it explicitly in Party Accounts.`,
      );
      setPreview(null);
      onAccepted?.(result);
    } catch (e) {
      if (seq === generation.current) setError(e.message);
    } finally {
      if (seq === generation.current) setBusy(false);
    }
  }
  const history = creditHistory.length>0 ? <div className="space-y-2 text-sm"><h3 className="font-semibold">Accepted credit-note history</h3>{creditHistory.map(c=><p key={c.id}>{c.noteNumber} · {c.voucherNumber} · {c.date} · goods ₹{c.amounts.goods}</p>)}</div> : null;
  if (acceptedCredit || remaining.goods==='0.00')
    return (
      <section className="rounded-3xl border border-gray-100 bg-white p-6">
        <h2 className="text-xl font-semibold">Supplier credit accepted</h2>
        <p className="mt-2 text-sm">
          {acceptedCredit?.noteNumber} · {acceptedCredit?.voucherNumber} ·{" "}
          {acceptedCredit?.date}
        </p>
        {history}
        <p className="mt-2 text-sm text-gray-500">
          Check allocation or unapplied credit in Party Accounts. Acceptance
          does not prove receipt of a refund.
        </p>
      </section>
    );
  return (
    <details className="space-y-4 rounded-3xl border border-gray-100 bg-white p-6">
      <summary className="cursor-pointer text-xl font-semibold">
        Accept supplier credit note
      </summary>
      <p className="text-sm text-gray-600">
        Full or proportional partial credit. Enter amounts from the supplier’s actual
        document, not an expected refund. Remaining goods ₹{remaining.goods}, CGST ₹
        {remaining.cgst}, SGST ₹{remaining.sgst}, IGST ₹{remaining.igst}. GST must match the cumulative proportional allocation across this return; the final note clears remaining cents. Freight and
        original rounding are excluded. Line-specific/non-proportional disputes, commercial-only and
        combined-return notes remain blocked. Unaccepted goods remain in clearing; no write-off is assumed.
      </p>
      {history}
      {creditHold&&creditHold.goods!=='0.00'&&<p className="rounded-xl bg-amber-50 p-4 text-sm text-amber-900">Supplier {creditHold.status?.toLowerCase()} goods ₹{creditHold.goods} remain on hold. Only ₹{creditHold.availableCreditGoods} goods are available for credit review; resolve the hold separately before accepting the held portion.</p>}
      <p className="text-sm">
        Posting date: {postingDate}. No stock moves again. Supplier debit /
        clearing and input-tax credits are created together with an unapplied
        bill-wise credit reference.
      </p>
      {error && (
        <p role="alert" className="text-red-700">
          {error}
        </p>
      )}
      {notice && (
        <p role="status" className="text-green-700">
          {notice}
        </p>
      )}
      <form onSubmit={load} className="space-y-4">
        <fieldset
          disabled={busy || !!notice}
          className="grid gap-4 md:grid-cols-2"
        >
          <label>
            Supplier credit-note number
            <input
              required
              maxLength={100}
              className={control}
              value={form.noteNumber}
              onChange={(e) => change("noteNumber", e.target.value)}
            />
          </label>
          <label>
            Supplier credit-note date
            <input
              required
              type="date"
              className={control}
              value={form.noteDate}
              onChange={(e) => change("noteDate", e.target.value)}
            />
          </label>
          {["goods", "cgst", "sgst", "igst"].map((k) => (
            <label key={k}>
              Actual{" "}
              {k === "goods" ? "goods credit" : k.toUpperCase() + " credit"}
              <input
                required
                inputMode="decimal"
                pattern="[0-9]+(\.[0-9]{1,2})?"
                className={control}
                value={form[k]}
                onChange={(e) => change(k, e.target.value)}
              />
            </label>
          ))}
          <label className="md:col-span-2">
            Credit and tax review note
            <textarea
              required
              minLength={10}
              maxLength={1000}
              className="mt-2 min-h-24 w-full rounded-xl border border-gray-200 p-3"
              value={form.reviewNote}
              onChange={(e) => change("reviewNote", e.target.value)}
            />
          </label>
        </fieldset>
        <button
          disabled={busy || !!notice}
          className="h-11 rounded-xl bg-primary-700 px-5 font-medium text-white disabled:opacity-50"
        >
          Preview Supplier Credit
        </button>
      </form>
      {preview && (
        <div className="space-y-4">
          <p className="font-semibold">
            Total supplier credit: ₹{preview.snapshot.amounts.total}
          </p>
          {preview.snapshot.remainingAfter&&<p className="text-sm text-amber-900">Goods still awaiting supplier credit after this note: ₹{preview.snapshot.remainingAfter.goods}. This does not create a cash refund or allocate an existing bill.</p>}
          <div className="overflow-x-auto">
            <table>
              <thead>
                <tr>
                  <th>Ledger</th>
                  <th>Debit</th>
                  <th>Credit</th>
                </tr>
              </thead>
              <tbody>
                {(preview.lines || preview.snapshot.lines).map((l, i) => (
                  <tr key={i}>
                    <td>{l.ledgerName || l.ledgerId}</td>
                    <td>{l.debit}</td>
                    <td>{l.credit}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {preview.alreadyAccepted ? (
            <p role="status">
              This return already has an accepted credit. Reload the return to
              see its voucher.
            </p>
          ) : canPost ? (
            <>
              <label className="flex items-start gap-3 text-sm">
                <input
                  type="checkbox"
                  checked={creditVerified}
                  disabled={busy}
                  onChange={(e) => setCreditVerified(e.target.checked)}
                />
                I verified the actual supplier credit note, original invoice,
                full returned goods and no duplicate recognition.
              </label>
              <label className="flex items-start gap-3 text-sm">
                <input
                  type="checkbox"
                  checked={taxVerified}
                  disabled={busy}
                  onChange={(e) => setTaxVerified(e.target.checked)}
                />
                I verified the input-tax reversal treatment and amounts with the
                responsible accountant. This is not a GST return filing.
              </label>
              <button
                type="button"
                disabled={busy || !creditVerified || !taxVerified}
                onClick={accept}
                className="h-11 rounded-xl bg-primary-700 px-5 font-medium text-white disabled:opacity-50"
              >
                Accept Credit &amp; Post
              </button>
            </>
          ) : (
            <p className="text-sm text-gray-500">
              Accounting posting permission is required to accept this credit.
            </p>
          )}
        </div>
      )}
    </details>
  );
}
